/**
 * SentinelRegulatoryPanel — per-case state reporting fields.
 *
 * Two layers on one panel:
 *   1. Universal dimensions on the case itself (line of business, fraud/abuse
 *      vs waste, date detected, settlement) — these keep the Medicare essence:
 *      medicare is the default and nothing else changes for Medicare cases.
 *   2. Florida AFWAAR fields (state='FL' side record) — only shown once the
 *      case is D-SNP or Medicaid, since only those flow into the state report.
 *
 * Vocabularies come from the agency template via the vocab endpoint, so the
 * dropdowns can never drift from what the state accepts.
 */
import { useState, useEffect, useCallback } from 'react';
import { api, Hint } from './ui';

const LOB_OPTIONS = [
  ['medicare', 'Medicare'], ['dsnp', 'D-SNP'], ['medicaid', 'Medicaid'],
  ['commercial', 'Commercial'], ['other', 'Other'],
];
const CLASS_OPTIONS = [['fraud_abuse', 'Fraud / Abuse'], ['waste', 'Waste']];

export default function SentinelRegulatoryPanel({ caseId, caseRow, onChanged }) {
  const [vocab, setVocab] = useState(null);
  const [reg, setReg] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');

  const isReportable = ['dsnp', 'medicaid'].includes(caseRow.line_of_business);

  const load = useCallback(async () => {
    try {
      const [v, r] = await Promise.all([
        api('/reports/afwaar/vocab'),
        api(`/cases/${caseId}/regulatory`),
      ]);
      setVocab(v.vocab || null);
      setReg(r.data || {});
    } catch { /* panel stays minimal on failure */ }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const saveCaseField = async (patch) => {
    setSaving(true); setErr('');
    try { await api(`/cases/${caseId}`, { method: 'PATCH', body: patch }); setSavedAt(new Date()); onChanged(); }
    catch (e) { setErr(e.message); }
    setSaving(false);
  };
  const saveReg = async (patch) => {
    setSaving(true); setErr('');
    try {
      const r = await api(`/cases/${caseId}/regulatory`, { method: 'PATCH', body: patch });
      setReg(r.data || {});
      setSavedAt(new Date());
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const selCls = 'w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-red-500/30 focus:border-red-400 outline-none bg-white';
  const labCls = 'text-[10px] font-semibold text-slate-500 uppercase tracking-wide';

  const Field = ({ label, children }) => (
    <div>
      <div className={labCls}>{label}</div>
      {children}
    </div>
  );
  const Sel = ({ value, onSave, options, placeholder = '— select —' }) => (
    <select value={value || ''} onChange={e => onSave(e.target.value || null)} className={selCls} disabled={saving}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <h2 className="font-semibold text-slate-800 flex items-center">
          Regulatory reporting
          <Hint text="Line of business, fraud/abuse vs waste, and the state report fields. Medicare cases stay Medicare and are never pulled into a state Medicaid report. D-SNP and Medicaid cases feed the annual Florida FWA Activity Report on the Reports tab." />
        </h2>
        <span className="text-xs text-slate-400">
          {LOB_OPTIONS.find(([v]) => v === caseRow.line_of_business)?.[1] || 'Medicare'}
          {isReportable ? ' · in state report scope' : ''} {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {err && <div className="p-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">{err}</div>}
          {savedAt && <div className="text-[11px] text-emerald-600">Saved {savedAt.toLocaleTimeString()}</div>}

          {/* Universal dimensions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Line of business">
              <select value={caseRow.line_of_business || 'medicare'} onChange={e => saveCaseField({ line_of_business: e.target.value })} className={selCls} disabled={saving}>
                {LOB_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Case class">
              <select value={caseRow.case_class || 'fraud_abuse'} onChange={e => saveCaseField({ case_class: e.target.value })} className={selCls} disabled={saving}>
                {CLASS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Date detected">
              <input type="date" defaultValue={caseRow.date_detected ? String(caseRow.date_detected).slice(0, 10) : ''}
                onBlur={e => e.target.value && saveCaseField({ date_detected: e.target.value })} className={selCls} disabled={saving} />
            </Field>
            <Field label="Settlement amount ($)">
              <input type="number" defaultValue={caseRow.settlement_amount ?? ''}
                onBlur={e => e.target.value !== '' && saveCaseField({ settlement_amount: e.target.value })} className={selCls} disabled={saving} />
            </Field>
          </div>

          {/* Florida AFWAAR fields — only meaningful for report-scope cases */}
          {isReportable && vocab && (
            <div className="border-t border-slate-100 pt-4">
              <div className="text-xs font-semibold text-slate-600 mb-2">Florida AFWAAR fields (report 0133)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="MPI Complaint ID (required)">
                  <input defaultValue={reg.mpi_complaint_id || ''} placeholder="9 digits"
                    onBlur={e => e.target.value !== (reg.mpi_complaint_id || '') && saveReg({ mpi_complaint_id: e.target.value || null })} className={selCls} disabled={saving} />
                </Field>
                <Field label="Date reported to MPI (15-day)">
                  <input type="date" defaultValue={reg.date_reported_to_mpi || ''}
                    onBlur={e => e.target.value && saveReg({ date_reported_to_mpi: e.target.value })} className={selCls} disabled={saving} />
                </Field>
                <Field label="Allegation type"><Sel value={reg.allegation_type} onSave={v => saveReg({ allegation_type: v })} options={vocab.allegation_type} /></Field>
                <Field label="Primary allegation"><Sel value={reg.primary_allegation} onSave={v => saveReg({ primary_allegation: v })} options={vocab.allegations} /></Field>
                <Field label="Secondary allegation"><Sel value={reg.secondary_allegation} onSave={v => saveReg({ secondary_allegation: v })} options={vocab.allegations} /></Field>
                <Field label="Detection tool"><Sel value={reg.detection_tool} onSave={v => saveReg({ detection_tool: v })} options={vocab.detection_tool} /></Field>
                <Field label="Source"><Sel value={reg.source} onSave={v => saveReg({ source: v })} options={vocab.source} /></Field>
                <Field label="Entity under review"><Sel value={reg.entity_under_review} onSave={v => saveReg({ entity_under_review: v })} options={vocab.entity_under_review} /></Field>
                <Field label="Provider type (PML)"><Sel value={reg.provider_type_code} onSave={v => saveReg({ provider_type_code: v })} options={vocab.provider_type} /></Field>
                <Field label="Vendor associated"><Sel value={reg.vendor_associated} onSave={v => saveReg({ vendor_associated: v })} options={vocab.vendor_associated} /></Field>
                <Field label="Report status"><Sel value={reg.status_label} onSave={v => saveReg({ status_label: v })} options={vocab.status} /></Field>
                <Field label="Closure disposition"><Sel value={reg.closure_disposition} onSave={v => saveReg({ closure_disposition: v })} options={vocab.closure_disposition} /></Field>
                <Field label="Corrective action"><Sel value={reg.corrective_action} onSave={v => saveReg({ corrective_action: v })} options={vocab.corrective_action} /></Field>
                <Field label="Reported to MFCU">
                  <Sel value={reg.reported_to_mfcu === true ? 'Yes' : reg.reported_to_mfcu === false ? 'No' : ''}
                    onSave={v => saveReg({ reported_to_mfcu: v === 'Yes' })} options={['Yes', 'No']} />
                </Field>
                <Field label="Date reported to MFCU">
                  <input type="date" defaultValue={reg.date_reported_to_mfcu || ''}
                    onBlur={e => e.target.value && saveReg({ date_reported_to_mfcu: e.target.value })} className={selCls} disabled={saving} />
                </Field>
                <Field label="Other entities reported to (up to 4)">
                  <div className="space-y-1">
                    {[0, 1, 2, 3].map(i => (
                      <Sel key={i} value={(reg.other_entities || [])[i]}
                        onSave={v => {
                          const arr = [...(reg.other_entities || [])];
                          arr[i] = v;
                          saveReg({ other_entities: arr.filter(Boolean) });
                        }}
                        options={vocab.other_entity} placeholder={`Entity ${i + 1}`} />
                    ))}
                  </div>
                </Field>
              </div>
              <div className="mt-3">
                <div className={labCls}>Detailed update override (leave blank to auto-compose quarterly updates from the case timeline)</div>
                <textarea rows={3} defaultValue={reg.detailed_update_override || ''}
                  onBlur={e => e.target.value !== (reg.detailed_update_override || '') && saveReg({ detailed_update_override: e.target.value || null })}
                  className={selCls + ' resize-none'} disabled={saving} />
              </div>
            </div>
          )}
          {!isReportable && (
            <p className="text-[11px] text-slate-400">Florida report fields appear once the line of business is D-SNP or Medicaid.</p>
          )}
        </div>
      )}
    </div>
  );
}
